/**
 * Helpers for filtering project work rows by calendar date (YYYY-MM-DD).
 */

/** First and last calendar day of the month containing `ref` (local timezone), as YYYY-MM-DD. */
export function getLocalCalendarMonthBounds(ref = new Date()) {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const endDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { startDate, endDate };
}

/**
 * Resolves API fetch bounds: empty start+end → current calendar month;
 * partial input → fills with month bounds around the set side.
 */
export function resolveWorkFetchDateRange(workRangeStart, workRangeEnd) {
  if (!workRangeStart && !workRangeEnd) {
    return getLocalCalendarMonthBounds();
  }
  let s = workRangeStart || null;
  let e = workRangeEnd || null;
  if (s && !e) {
    e = getLocalCalendarMonthBounds(new Date(`${s}T12:00:00`)).endDate;
  } else if (!s && e) {
    s = getLocalCalendarMonthBounds(new Date(`${e}T12:00:00`)).startDate;
  }
  if (s && e && s > e) {
    const t = s;
    s = e;
    e = t;
  }
  return { startDate: s, endDate: e };
}

export function workDateKey(work_date) {
  if (work_date == null) return null;
  if (typeof work_date === 'string') {
    const m = work_date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = new Date(work_date);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

export function filterWorkDetailsByDateRange(details, start, end) {
  if (!details?.length) return [];
  let s = start || null;
  let e = end || null;
  if (s && e && s > e) {
    const t = s;
    s = e;
    e = t;
  }
  return details.filter((d) => {
    const key = workDateKey(d.work_date);
    if (!key) return false;
    if (s && key < s) return false;
    if (e && key > e) return false;
    return true;
  });
}

export function durationStringToSeconds(str) {
  if (!str || typeof str !== 'string') return 0;
  const parts = str.split(':').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 0;
  const [hours, minutes, seconds] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

export function secondsToDurationString(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function sumDurationSeconds(details, predicate) {
  return (details || []).reduce((acc, d) => {
    if (typeof predicate === 'function' && !predicate(d)) return acc;
    return acc + durationStringToSeconds(d.work_duration);
  }, 0);
}
